import { format, formatDuration, intervalToDuration, isAfter, Locale } from "date-fns";
import { CourseListItem } from "@/features/courses/course-interfaces";
import { Booking, BookingRoot } from "@/features/user/booking-interfaces";
import { usePaymentStore } from "@/store/use-payment-store";
import { Location } from "@/types/sessionInterfaces";

type Distance = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

type Duration = {
  hours: number;
  minutes: number;
  seconds: number;
};

type ActivityData = {
  courses: CourseListItem[];
};

type Facility = {
  value: string;
  label: string;
};

type TranslateFn = (key: string) => string;

type BookingWithPaymentDeadline = Pick<Booking, "lastPaymentTime">;

export const getDuration = (occasionDate: string, t: TranslateFn) => {
  if (!occasionDate) return "";

  const now = new Date();
  const occasionTime = new Date(occasionDate);
  const isPastOccasion = isAfter(now, occasionTime);

  if (isPastOccasion) return "";
  const raw = intervalToDuration({
    start: now,
    end: occasionTime
  });

  let distance: Distance = {
    days: (raw.days ?? 0) as number,
    hours: (raw.hours ?? 0) as number,
    minutes: (raw.minutes ?? 0) as number,
    seconds: (raw.seconds ?? 0) as number
  };

  if (
    distance.minutes === 0 &&
    distance.hours === 0 &&
    distance.days === 0 &&
    distance.seconds >= 60
  ) {
    const totalMinutes = Math.floor(distance.seconds / 60);
    distance = {
      days: 0,
      hours: Math.floor(totalMinutes / 60),
      minutes: totalMinutes % 60,
      seconds: distance.seconds % 60
    };
    if (distance.hours >= 24) {
      distance.days = Math.floor(distance.hours / 24);
      distance.hours = distance.hours % 24;
    }
  }

  const parts = [];

  if (distance.days > 0) {
    parts.push(`${distance.days} ${distance.days === 1 ? t("day") : t("days")}`);
  }
  if (distance.hours > 0) {
    parts.push(`${distance.hours} ${distance.hours === 1 ? t("hour") : t("hours")}`);
  }
  if (distance.minutes > 0) {
    parts.push(`${distance.minutes} ${distance.minutes === 1 ? t("minute") : t("minutes")}`);
  }
  if (
    distance.days === 0 &&
    distance.hours === 0 &&
    distance.minutes === 0 &&
    distance.seconds > 0
  ) {
    parts.push(`1 ${t("minute")}`);
  }
  if (
    distance.days === 0 &&
    distance.hours === 0 &&
    distance.minutes === 0 &&
    distance.seconds === 0
  ) {
    return "";
  }
  return parts.join(" ");
};

export const unpaid = (upcomingData?: BookingRoot) => {
  if (upcomingData?.mybookings) {
    const findNotPaidBookings = upcomingData?.mybookings?.filter(
      (booking: Booking) => !booking.isPaid && !booking.isCancelled
    );
    return findNotPaidBookings;
  }
};

export type PaymentBooking = Pick<Booking, "isPaid" | "isCancelled" | "isQueue" | "lastPaymentTime"> & {
  activity: Pick<Booking["activity"], "name"> & {
    occasions?: readonly { at: string }[] | null;
  };
  occasion?: { at?: string | null } | null;
};

export type PaymentResultState = "pending" | "required" | "not-required";

export type ReservationPaymentMatch = "needs-payment" | "settled-without-payment" | "unknown";

/** Same rule as the cart payment reminder: unpaid and the deadline is still in the future. */
export const bookingRequiresPayment = (
  booking: Pick<PaymentBooking, "isPaid" | "isCancelled" | "isQueue" | "lastPaymentTime">,
  now: Date
) => {
  if (booking.isPaid || booking.isCancelled) return false;
  if (!booking.lastPaymentTime) return false;
  return new Date(booking.lastPaymentTime).getTime() > now.getTime();
};

const occasionMinuteKey = (value: Date) => {
  const pad = (part: number) => String(part).padStart(2, "0");
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}T${pad(value.getHours())}:${pad(value.getMinutes())}`;
};

export const occasionsMatch = (left: Date, right: string) => {
  const rightDate = new Date(right);
  if (Number.isNaN(left.getTime()) || Number.isNaN(rightDate.getTime())) return false;
  if (left.getTime() === rightDate.getTime()) return true;
  return occasionMinuteKey(left) === occasionMinuteKey(rightDate);
};

const bookingOccasionTimes = (booking: PaymentBooking) => {
  const times = booking.activity.occasions?.map((item) => item.at) ?? [];
  if (booking.occasion?.at) times.push(booking.occasion.at);
  return times;
};

const bookingMatchesOccasion = (booking: PaymentBooking, occasion: Date) =>
  bookingOccasionTimes(booking).some((at) => occasionsMatch(occasion, at));

/**
 * Match this reservation so an older unpaid booking does not change a free booking's result.
 * Courses often have no single `occasion.at`; their times live on `activity.occasions`.
 */
export const classifyReservationPayment = (
  bookings: readonly PaymentBooking[] | undefined,
  activityName: string,
  occasion: Date,
  price: number,
  now: Date
): ReservationPaymentMatch => {
  if (!bookings) return "unknown";

  const named = bookings.filter(
    (booking) => booking.activity.name.trim() === activityName.trim() && !booking.isCancelled
  );
  const timed = named.filter((booking) => bookingMatchesOccasion(booking, occasion));
  const candidates = timed.length > 0 ? timed : price > 0 ? named : [];

  if (candidates.some((booking) => bookingRequiresPayment(booking, now))) return "needs-payment";
  if (timed.length > 0) return "settled-without-payment";
  if (price > 0 && named.length > 0 && named.every((booking) => booking.isPaid)) {
    return "settled-without-payment";
  }
  return "unknown";
};

export const findReservedBookingNeedingPayment = (
  bookings: readonly PaymentBooking[] | undefined,
  activityName: string,
  occasion: Date,
  now: Date
) =>
  bookings?.find(
    (booking) =>
      booking.activity.name.trim() === activityName.trim() &&
      bookingMatchesOccasion(booking, occasion) &&
      bookingRequiresPayment(booking, now)
  );

export const paymentResultState = ({
  isEnqueue,
  price,
  bookingsSettled,
  match
}: {
  isEnqueue: boolean;
  price: number;
  bookingsSettled: boolean;
  match: ReservationPaymentMatch;
}): PaymentResultState => {
  if (match === "needs-payment") return "required";
  if (isEnqueue) return "not-required";
  if (!bookingsSettled && price > 0) return "pending";
  if (bookingsSettled && price > 0 && match === "unknown") return "required";
  return "not-required";
};

let _paymentIntervalId: ReturnType<typeof setTimeout> | null = null;

const clearPaymentInterval = () => {
  if (_paymentIntervalId !== null) {
    clearTimeout(_paymentIntervalId);
    _paymentIntervalId = null;
  }
};

export const sortUpcomingBookings = (
  unpaidBookings: BookingWithPaymentDeadline[] | undefined,
  t: TranslateFn,
  refetch: () => void
) => {
  if (unpaidBookings && unpaidBookings.length > 0) {
    const sorted = unpaidBookings.toSorted((a, b) => {
      if (a.lastPaymentTime && b.lastPaymentTime) {
        const dateA = new Date(a.lastPaymentTime).getTime();
        const dateB = new Date(b.lastPaymentTime).getTime();
        return dateA - dateB;
      } else if (a.lastPaymentTime) {
        return -1;
      } else if (b.lastPaymentTime) {
        return 1;
      }
      return 0;
    });

    const now = new Date();
    const futureBookings = sorted.filter(
      (booking) => booking.lastPaymentTime && new Date(booking.lastPaymentTime) > now
    );
    if (futureBookings.length === 0) {
      usePaymentStore.setState({ nextPayment: "" });
      return clearPaymentInterval;
    }

    let currentIndex = 0;
    let currentPaymentTime = futureBookings[currentIndex]?.lastPaymentTime;
    if (!currentPaymentTime) {
      usePaymentStore.setState({ nextPayment: "" });
      return clearPaymentInterval;
    }
    let deadlineMs = new Date(currentPaymentTime).getTime();

    usePaymentStore.setState({ nextPayment: getDuration(currentPaymentTime, t) });

    const tick = () => {
      if (!currentPaymentTime) return;
      const now = new Date();
      const durationText = getDuration(currentPaymentTime, t);
      if (isAfter(now, new Date(currentPaymentTime))) {
        currentIndex += 1;
        if (currentIndex >= futureBookings.length) {
          usePaymentStore.setState({ nextPayment: "" });
          clearPaymentInterval();
          refetch();
          return;
        }
        currentPaymentTime = futureBookings[currentIndex]?.lastPaymentTime;
        if (!currentPaymentTime) {
          usePaymentStore.setState({ nextPayment: "" });
          clearPaymentInterval();
          refetch();
          return;
        }
        deadlineMs = new Date(currentPaymentTime).getTime();
        usePaymentStore.setState({ nextPayment: getDuration(currentPaymentTime, t) });

        const msLeft = deadlineMs - now.getTime();
        _paymentIntervalId = setTimeout(tick, msLeft > 60_000 ? 1000 : 500);
        return;
      }
      usePaymentStore.setState({ nextPayment: durationText });
      const msLeft = deadlineMs - now.getTime();
      _paymentIntervalId = setTimeout(tick, msLeft > 60_000 ? 1000 : 500);
    };
    _paymentIntervalId = setTimeout(tick, 1000);
  }

  return clearPaymentInterval;
};

type SessionFacilitiesData = {
  locations?: Location[];
};

export const getFacilities = (
  data: ActivityData | SessionFacilitiesData | undefined,
  isCourse: boolean
) => {
  if (isCourse) {
    if (!data || !("courses" in data) || !data.courses) return undefined;
    const courseFacilities = data.courses.reduce((acc: Facility[], curr: CourseListItem) => {
      if (
        curr?.activity?.location.name &&
        !acc.some((facility) => facility?.label === curr?.activity?.location?.name)
      ) {
        acc.push({
          value: curr?.activity?.location?.name.toLowerCase(),
          label: curr?.activity?.location?.name
        });
      }
      return acc;
    }, []);
    return courseFacilities;
  }
  if (!data || !("locations" in data) || !data.locations) return undefined;
  const sessionsFacilities = data.locations.reduce((acc: Facility[], curr: Location) => {
    if (curr?.name && !acc.some((facility: Facility) => facility?.label === curr?.name)) {
      acc.push({
        value: curr?.name.toLowerCase(),
        label: curr?.name
      });
    }
    return acc;
  }, []);
  return sessionsFacilities;
};

export const isPWA = () => {
  if (typeof window !== "undefined") {
    const standaloneNavigator = window.navigator as Navigator & { standalone?: boolean };
    return (
      standaloneNavigator.standalone === true || // iOS PWA Standalone
      document.referrer.includes("android-app://") || // Android Trusted Web App
      ["fullscreen", "standalone", "minimal-ui"].some(
        (displayMode) => window.matchMedia("(display-mode: " + displayMode + ")").matches
      )
    );
  }
};

export const updateServiceWorker = (appUrlOverride: string) => {
  const path = window.location.href.split("/membersapp")?.[0];
  const hasDomainChanged = appUrlOverride !== "x" && path !== appUrlOverride;
  const hasUpdateUrl = window.location.href.includes("update");
  if (hasDomainChanged && !hasUpdateUrl && isPWA()) {
    window.location.assign(`${path}/membersapp/update`);
    return;
  }
};

export function dateVariableFormat(date: Date) {
  const newDate = new Date(date.setSeconds(0));
  return format(newDate, "yyyy-MM-dd'T'HH:mm:ss");
}

export function humanizeDuration(duration: string | Duration, locale: Locale) {
  if (typeof duration === "string") {
    const time = duration.split(":");
    return formatDuration(
      {
        hours: parseInt(time[0] ?? "0"),
        minutes: parseInt(time[1] ?? "0"),
        seconds: parseInt(time[2] ?? "0")
      },
      { delimiter: ", ", locale }
    );
  } else if (duration.hours >= 0 && duration?.minutes >= 0) {
    return formatDuration(
      {
        hours: duration.hours,
        minutes: duration?.minutes || 0
      },
      { delimiter: ", ", locale }
    );
  }
}

export const convertFileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
};

export const getNameInitials = (name: string): string => {
  if (!name) return "";
  const nameParts = name.trim().split(" ");
  const firstInitial = nameParts[0]?.[0] || "";
  const lastInitial = nameParts.length > 1 ? nameParts[nameParts.length - 1]?.[0] || "" : "";
  return (firstInitial + lastInitial).toUpperCase();
};

export const handleHtmlClassChange = (newTheme: string) => {
  if (typeof window !== "undefined") {
    const html = document.documentElement;
    if (newTheme === "dark") {
      html.classList.add("dark");
    } else {
      html.classList.remove("dark");
    }
  }
};

export const isIOS = (): boolean => {
  if (typeof window === "undefined") return false;

  return /iPad|iPhone|iPod/.test(navigator.userAgent);
};

export function parseCurrency(currencyString: string): number {
  if (!currencyString || typeof currencyString !== "string") return 0;

  // Grab the first chunk that looks like a number (keeps digits, comma, dot, minus)
  const numeric = currencyString.match(/-?[\d.,\s]+/)?.[0]?.replace(/\s+/g, "") || "";
  if (!numeric) return 0;

  // Decide decimal separator by whichever (comma or dot) appears last
  const lastComma = numeric.lastIndexOf(",");
  const lastDot = numeric.lastIndexOf(".");
  const decimalSep = lastComma > lastDot ? "," : lastDot > -1 ? "." : "";

  const normalized = decimalSep
    ? (() => {
        const parts = numeric.split(decimalSep);
        const fraction = parts.pop() ?? "";
        const integer = parts.join("").replace(/[.,]/g, ""); // remove thousand separators
        return `${integer}.${fraction}`;
      })()
    : numeric.replace(/[.,]/g, ""); // no decimal separator found

  const value = Number(normalized);
  return Number.isFinite(value) ? value : 0;
}

export async function checkImageExists(url: string): Promise<boolean> {
  try {
    const response = await fetch(url, { method: "GET" });
    return response.ok;
  } catch {
    return false;
  }
}
